import { MessageSquare } from 'lucide-react'
import {
  Button,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  Textarea,
} from '@/shared/ui/primitives'
import { useFeedbackDialogContext } from '../providers'
import { useFeedbackDialog } from '../hooks'
import { FeedbackTypeSelector } from './feedback-type-selector'
import { FeedbackFeatureSelect } from './feedback-feature-select'
import { FeedbackEnvironmentInfo } from './feedback-environment-info'
import { FeedbackAttachmentPicker } from './feedback-attachment-picker'

export function FeedbackDialog() {
  const { isOpen, closeDialog, onSubmitFeedback, onUploadFeedbackAttachment, notifySubmitted } =
    useFeedbackDialogContext()
  const {
    feedbackType,
    selectedFeature,
    message,
    isSubmitting,
    featureOptions,
    environmentDetails,
    selectedFile,
    attachmentPreviewUrl,
    handleTypeChange,
    handleFeatureChange,
    handleMessageChange,
    handleFileSelect,
    handleClearAttachment,
    handleSubmit,
  } = useFeedbackDialog({
    onSubmitFeedback,
    onUploadFeedbackAttachment,
    isOpen,
    onClose: closeDialog,
    onSubmitted: notifySubmitted,
  })

  return (
    <Dialog open={isOpen} onOpenChange={(open) => { if (!open) closeDialog() }}>
      <DialogContent className="sm:max-w-[480px]">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <MessageSquare className="size-5" />
            Send Feedback
          </DialogTitle>
          <DialogDescription>
            Help us improve BlindBook. Tell us what&apos;s on your mind.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-4">
          <FeedbackTypeSelector value={feedbackType} onChange={handleTypeChange} />
          <FeedbackFeatureSelect value={selectedFeature} options={featureOptions} onChange={handleFeatureChange} />
          <div className="space-y-1.5">
            <label htmlFor="feedback-message" className="text-sm font-medium">Message</label>
            <Textarea
              id="feedback-message"
              rows={6}
              placeholder="Describe your feedback..."
              value={message}
              onChange={(e) => handleMessageChange(e.target.value)}
              disabled={isSubmitting}
            />
            <p className="text-xs text-muted-foreground text-right">{message.length} / 1000</p>
          </div>
          <FeedbackAttachmentPicker
            previewUrl={attachmentPreviewUrl}
            fileName={selectedFile?.name ?? null}
            disabled={isSubmitting}
            onSelect={handleFileSelect}
            onClear={handleClearAttachment}
          />
          <FeedbackEnvironmentInfo
            browser={environmentDetails.browser}
            platform={environmentDetails.platform}
            screenResolution={environmentDetails.screenResolution}
          />
        </div>
        <DialogFooter className="flex justify-end gap-2">
          <Button variant="outline" onClick={closeDialog} disabled={isSubmitting}>
            Cancel
          </Button>
          <Button onClick={handleSubmit} disabled={isSubmitting}>
            {isSubmitting ? 'Sending...' : 'Send Feedback'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
